import { useState } from 'react';
import API from '@/lib/api';
import { Pencil, Check, Target } from 'lucide-react';
import { toast } from 'sonner';

const R = 46;
const C = 2 * Math.PI * R;

export function OfferGoalRing({ week, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const count = week?.count || 0;
  const target = week?.target || 0;
  const pct = target ? Math.min(1, count / target) : 0;
  const done = target && count >= target;
  const color = done ? '#10B981' : pct >= 0.5 ? '#22D3EE' : '#8B5CF6';

  const save = async () => {
    const n = parseInt(val, 10);
    if (Number.isNaN(n) || n < 0) return toast.error('Enter a number');
    try { await API.put('/dashboard/offer-goal', { weekly_target: n }); setEditing(false); onSaved?.(); toast.success('Weekly goal saved'); }
    catch { toast.error('Could not save goal'); }
  };

  return (
    <div className="flex items-center gap-4 rounded-xl border border-[#1F293D] bg-[#0F1623] p-4 sm:col-span-3 lg:col-span-1 lg:flex-col lg:items-center lg:text-center max-sm:col-span-2" data-testid="offer-goal">
      <div className="relative h-28 w-28 shrink-0">
        <svg viewBox="0 0 112 112" className="h-full w-full -rotate-90">
          <circle cx="56" cy="56" r={R} fill="none" stroke="#1E293B" strokeWidth="10" />
          <circle cx="56" cy="56" r={R} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - pct)}
            style={{ transition: 'stroke-dashoffset 700ms ease-out', filter: `drop-shadow(0 0 6px ${color})` }} />
        </svg>
        <div className="absolute inset-0 grid place-items-center">
          <div className="text-center leading-tight">
            <p className="text-2xl font-bold tabular-nums text-white" data-testid="offer-goal-count">{count}<span className="text-sm text-slate-500">/{target || '–'}</span></p>
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{target ? `${Math.round(pct * 100)}%` : 'no goal'}</p>
          </div>
        </div>
      </div>
      <div className="min-w-0 space-y-1.5">
        <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 lg:justify-center"><Target className="h-3 w-3" />This week's goal</p>
        {editing ? (
          <div className="flex items-center gap-1.5">
            <input autoFocus value={val} onChange={e => setVal(e.target.value.replace(/\D/g, ''))} onKeyDown={e => e.key === 'Enter' && save()} inputMode="numeric"
              className="h-8 w-20 rounded-md border border-[#334155] bg-[#0B0F17] px-2 text-sm text-white" placeholder="e.g. 20" data-testid="offer-goal-input" />
            <button onClick={save} className="grid h-8 w-8 place-items-center rounded-md bg-[#3B82F6] text-white" data-testid="offer-goal-save"><Check className="h-4 w-4" /></button>
          </div>
        ) : (
          <button onClick={() => { setVal(target ? String(target) : ''); setEditing(true); }} className="flex items-center gap-1 text-xs text-blue-300 hover:text-blue-200" data-testid="offer-goal-edit">
            <Pencil className="h-3 w-3" />{target ? `${target} offers / week` : 'Set a weekly goal'}
          </button>
        )}
        <p className="text-[11px] text-slate-500" data-testid="offer-goal-status">{done ? 'Goal hit — nice work!' : target ? `${target - count} more to go · resets Monday` : 'Mon–Sun, resets weekly'}</p>
      </div>
    </div>
  );
}
